import React, { useMemo, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import classnames from 'classnames';
import { Participant } from 'twilio-video';
import { useTranslation } from 'react-i18next';

import {
  useDialog,
  useIsGuestCall,
  useParticipants,
  useRoomHost,
  useRoomState,
  useRoomRightSidebar,
  useScreenShareParticipant,
  useScreenSize,
  useTeleconsent,
} from 'hooks';
import {
  EMDRButton,
  RecordingButton,
  SessionTimerButton,
  ToggleAudioButton,
  ToggleBackgroundEffectsButton,
  ToggleChatButton,
  ToggleFaxesButton,
  ToggleNotesButton,
  ToggleParticipantsButton,
  ToggleScreenShareButton,
  ToggleSettingsButton,
  ToggleVideoButton,
  WhiteboardButton,
} from 'components';
import { DotMenu } from '../DotMenu';
import { EndCall } from '../EndCall';
import { ConnectionTestDialog } from 'components/ConnectionTest/ConnectionTestDialog';
import { InCallFaxesDialog } from '../InCallFaxesDialog';
import { getIsAdminByRoles, parseParticipantIdentity } from 'utils';
import { TeleconsentWizard } from 'components/Teleconsent';
import { getAllFormKeys } from 'components/Teleconsent/forms/dynamicRegistry';
import { FORM_REGISTRY } from 'components/Teleconsent/forms/registry';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

import styles from './MainControls.module.scss';
import { useCanShowRecordingButton } from 'types/useCanShowRecordingButton';
import { isHipaaLink } from 'config/productProfile';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';

export function MainControls(): JSX.Element {
  const { t } = useTranslation('providerRoom');
  const user = useAppSelector(selectUser);
  const { isMobile } = useScreenSize();
  const isGuestCall = useIsGuestCall();
  const [isFaxesDialogOpen, setFaxesDialogOpen] = useState(false);
  const [connectionTestOpen, setConnectionTestOpen] = useState(false);
  const isMember = user?.clinicUser?.via3UserType === 'Member';
  const canAccessFaxes = !isHipaaLink && !isGuestCall && !!user?.clinicUser?.canAccessFaxes && !isMember;
  const isAdmin = getIsAdminByRoles(user?.clinicUser?.roles ?? []);
  const { showDialog } = useDialog();
  const participants = useParticipants();
  const forceLightThemeForGuest = isGuestCall && localStorage.getItem('app-theme') === null;
  const roomState = useRoomState();
  const { isHost, hostId } = useRoomHost();
  const { isOpen: isTeleconsentOpen, startSession: startTeleconsentSession } = useTeleconsent();
  const disabledSystemFormKeys = user?.clinicUser?.teleconsentDisabledForms ?? [];
  const systemFormKeys = Object.keys(FORM_REGISTRY);
  // teleconsentDisabledForms only controls system forms; custom forms are pre-filtered to enabled-only in the registry
  const enabledFormKeys = getAllFormKeys().filter(
    (k) => !systemFormKeys.includes(k) || !disabledSystemFormKeys.includes(k),
  );
  const canUseTeleconsent =
    isHost &&
    !!user?.clinicUser?.teleconsentEnabled &&
    (isAdmin || !!user?.clinicUser?.teleconsentActivateForAllStaff) &&
    !isTeleconsentOpen;

  const handleOpenTeleconsentWizard = (): void => {
    const wizardParticipants = participants.map((p) => ({
      identity: p.identity,
      displayName: parseParticipantIdentity(p)[1] ?? p.identity,
    }));
    const modal: { destroy: () => void; update: (p: any) => void } = showDialog(TeleconsentWizard, {
      participants: wizardParticipants,
      enabledFormKeys,
      onBegin: async (
        formKey: string,
        participantIdentity: string | null,
        providerPrefill?: Record<string, string>,
        participantDisplayName?: string,
        providerName?: string,
        clientLabel?: string,
      ) => {
        modal.destroy();
        await startTeleconsentSession(
          formKey,
          participantIdentity,
          providerPrefill,
          participantDisplayName,
          providerName,
          clientLabel,
        );
      },
      onCancel: () => modal.destroy(),
    });
  };
  const canShowRecordingButton = useCanShowRecordingButton();
  const { hideSidebar, toggleSettings, toggleChat, toggleParticipants, toggleNotes, toggleBackgroundEffects } =
    useRoomRightSidebar();
  const isReconnecting = roomState === 'reconnecting';
  const participantDisplayingSharedScreen: Participant | undefined = useScreenShareParticipant();
  const sharingDisabled: boolean = useMemo(() => {
    const [identityDisplayingSharedScreenId] = participantDisplayingSharedScreen
      ? parseParticipantIdentity(participantDisplayingSharedScreen)
      : [];

    return !isHost && identityDisplayingSharedScreenId === hostId;
  }, [participantDisplayingSharedScreen, isHost]);

  const isScreenShareSupported: boolean = navigator.mediaDevices && !!navigator.mediaDevices.getDisplayMedia;

  const handleEndCall = (): void => {
    hideSidebar();
  };

  return (
    <Stack
      className={classnames(styles.container, {
        [styles.containerMobile]: isMobile,
        [styles.guestLight]: forceLightThemeForGuest,
      })}
      direction='row'
    >
      <Stack className={styles.controlWithLabel}>
        <ToggleAudioButton
          className={styles.deviceButton}
          iconClassName={styles.audioIcon}
          disabled={isReconnecting}
          withTooltip
        />
        {!isMobile && (
          <Typography component='p' className={styles.controlLabel}>
            {t('mic')}
          </Typography>
        )}
      </Stack>

      <Stack className={styles.controlWithLabel}>
        <ToggleVideoButton
          className={styles.deviceButton}
          iconClassName={styles.videoIcon}
          disabled={isReconnecting}
          withTooltip
        />
        {!isMobile && (
          <Typography component='p' className={styles.controlLabel}>
            {t('camera')}
          </Typography>
        )}
      </Stack>

      <Stack className={styles.controlWithLabel}>
        <ToggleChatButton onClick={toggleChat} withTooltip />
        {!isMobile && (
          <Typography component='p' className={styles.controlLabel}>
            {t('chat')}
          </Typography>
        )}
      </Stack>

      <Stack className={styles.controlWithLabel}>
        <DotMenu
          className={styles.deviceButton}
          iconClassName={styles.moreIcon}
          iconColor={forceLightThemeForGuest ? '#FFF' : undefined}
          contentBgColor={forceLightThemeForGuest ? '#FFF' : 'var(--app-surface)'}
          popperClassName={isMobile ? styles.popperMobile : undefined}
          withTooltip
        >
          <Stack direction='column' spacing={isMobile ? '20px' : '10px'} alignItems='flex-start'>
            {!isMobile && (
              <Stack direction='column' spacing='10px' alignItems='center' width='100%'>
                {isScreenShareSupported && (
                  <ToggleScreenShareButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting || sharingDisabled}
                    withLabel
                  />
                )}

                {isHipaaLink && isHost && (
                  <EMDRButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                  />
                )}

                <ToggleBackgroundEffectsButton
                  className={styles.dotMenuDeviceButton}
                  iconClassName={styles.dotMenuShareIcon}
                  iconColor='var(--app-brand-primary)'
                  disabled={isReconnecting}
                  onClick={toggleBackgroundEffects}
                  withLabel
                />

                {isHost && (
                  <WhiteboardButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                  />
                )}

                {canAccessFaxes && (
                  <ToggleFaxesButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                    onClick={() => setFaxesDialogOpen(true)}
                  />
                )}

                {isHipaaLink && canShowRecordingButton && (
                  <RecordingButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                  />
                )}

                {canUseTeleconsent && (
                  <Box component='span' display='block' className={styles.dotMenuDeviceButton}>
                    <IconButton className={styles.dotMenuDeviceButton} onClick={handleOpenTeleconsentWizard}>
                      <AssignmentOutlinedIcon
                        className={styles.dotMenuShareIcon}
                        sx={{ color: 'var(--app-brand-primary)' }}
                      />
                      <Typography component='p' variant='body2' sx={{ color: 'var(--app-text-default)' }} ml='10px'>
                        {t('teleconsent')}
                      </Typography>
                    </IconButton>
                  </Box>
                )}

                <Box component='span' display='block' className={styles.dotMenuDeviceButton}>
                  <IconButton className={styles.dotMenuDeviceButton} onClick={() => setConnectionTestOpen(true)}>
                    <NetworkCheckIcon className={styles.dotMenuShareIcon} sx={{ color: 'var(--app-brand-primary)' }} />
                    <Typography component='p' variant='body2' sx={{ color: 'var(--app-text-default)' }} ml='10px'>
                      {t('connectionTest.menuLabel')}
                    </Typography>
                  </IconButton>
                </Box>
              </Stack>
            )}

            {isMobile && (
              <>
                {isHost && (
                  <>
                    <SessionTimerButton
                      disabled={isReconnecting}
                      className={styles.dotMenuDeviceButton}
                      iconColor='var(--app-brand-primary)'
                      iconClassName={styles.dotMenuShareIcon}
                      withLabel
                    />

                    <ToggleNotesButton
                      disabled={isReconnecting}
                      className={styles.dotMenuDeviceButton}
                      iconColor='var(--app-brand-primary)'
                      iconClassName={styles.dotMenuShareIcon}
                      onClick={toggleNotes}
                      withLabel
                    />
                  </>
                )}

                <ToggleParticipantsButton
                  disabled={isReconnecting}
                  className={styles.dotMenuDeviceButton}
                  iconColor='var(--app-brand-primary)'
                  iconClassName={styles.dotMenuShareIcon}
                  onClick={toggleParticipants}
                  withLabel
                />

                <ToggleBackgroundEffectsButton
                  className={styles.dotMenuDeviceButton}
                  iconClassName={styles.dotMenuShareIcon}
                  iconColor='var(--app-brand-primary)'
                  disabled={isReconnecting}
                  onClick={toggleBackgroundEffects}
                  withLabel
                />

                {isHost && (
                  <>
                    <WhiteboardButton
                      className={styles.dotMenuDeviceButton}
                      iconClassName={styles.dotMenuShareIcon}
                      iconColor='var(--app-brand-primary)'
                      disabled={isReconnecting}
                      withLabel
                    />

                    {isHipaaLink && isHost && (
                      <EMDRButton
                        className={styles.dotMenuDeviceButton}
                        iconClassName={styles.dotMenuShareIcon}
                        iconColor='var(--app-brand-primary)'
                        disabled={isReconnecting}
                        withLabel
                      />
                    )}
                  </>
                )}

                {canAccessFaxes && (
                  <ToggleFaxesButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                    onClick={() => setFaxesDialogOpen(true)}
                  />
                )}

                {isHipaaLink && canShowRecordingButton && (
                  <RecordingButton
                    className={styles.dotMenuDeviceButton}
                    iconClassName={styles.dotMenuShareIcon}
                    iconColor='var(--app-brand-primary)'
                    disabled={isReconnecting}
                    withLabel
                  />
                )}

                <ToggleSettingsButton
                  disabled={isReconnecting}
                  className={styles.dotMenuDeviceButton}
                  iconColor='var(--app-brand-primary)'
                  iconClassName={styles.dotMenuShareIcon}
                  onClick={toggleSettings}
                  withLabel
                />

                {canUseTeleconsent && (
                  <Box component='span' display='block' className={styles.dotMenuDeviceButton}>
                    <IconButton className={styles.dotMenuDeviceButton} onClick={handleOpenTeleconsentWizard}>
                      <AssignmentOutlinedIcon
                        className={styles.dotMenuShareIcon}
                        sx={{ color: 'var(--app-brand-primary)' }}
                      />
                      <Typography component='p' variant='body2' sx={{ color: 'var(--app-text-default)' }} ml='10px'>
                        {t('teleconsent')}
                      </Typography>
                    </IconButton>
                  </Box>
                )}

                <Box component='span' display='block' className={styles.dotMenuDeviceButton}>
                  <IconButton className={styles.dotMenuDeviceButton} onClick={() => setConnectionTestOpen(true)}>
                    <NetworkCheckIcon className={styles.dotMenuShareIcon} sx={{ color: 'var(--app-brand-primary)' }} />
                    <Typography component='p' variant='body2' sx={{ color: 'var(--app-text-default)' }} ml='10px'>
                      {t('connectionTest.menuLabel')}
                    </Typography>
                  </IconButton>
                </Box>
              </>
            )}
          </Stack>
        </DotMenu>
        {!isMobile && (
          <Typography component='p' className={styles.controlLabel}>
            {t('more')}
          </Typography>
        )}
      </Stack>

      <Stack className={styles.controlWithLabel}>
        <EndCall withTooltip disabled={isReconnecting} onEndCall={handleEndCall} />
        {!isMobile && (
          <Typography component='p' className={styles.controlLabel}>
            {t('endCall')}
          </Typography>
        )}
      </Stack>
      <InCallFaxesDialog open={isFaxesDialogOpen} onClose={() => setFaxesDialogOpen(false)} />
      <ConnectionTestDialog open={connectionTestOpen} onClose={() => setConnectionTestOpen(false)} />
    </Stack>
  );
}
